// Invoicing DTOs — BUILD_SPEC.md §5.7 (invoices+lines, credit_notes+lines), §8.4
// steps 4-5 (raise invoices, approve, credit note + restock), §7 (VAT). Owned by G15
// (plan.md) — this is the ONLY core file G15 fills; the barrel (schemas/index.ts)
// already re-exports it.
//
// `CREDIT_NOTE_STATUSES` / `XERO_SYNC_STATES` are defined LOCALLY rather than added
// to `@zebra/core/enums.ts` — this goal's S0 registry rule is "touch ONLY
// packages/db/src/schema/invoicing.ts (+rls), packages/core/src/schemas/invoicing.ts,
// services/api/src/modules/invoicing/, services/api/src/pdf/{invoice,credit-note}.ts,
// colocated tests"; enums.ts is G2's foundation file and out of scope here (same
// precedent as schemas/sales.ts's locally-defined INVOICING_MODES et al.).
// `INVOICE_STATUSES`/`VAT_TREATMENTS`, by contrast, ARE imported from `../enums.js`:
// those are existing, already-ratified core lists (same package, no barrel edit) —
// importing rather than re-declaring them under the same name is required here, not
// just stylistic: `packages/core/src/index.ts` re-exports both `./enums.js` and
// `./schemas/index.js`, so a second LOCAL `export const INVOICE_STATUSES` in this
// file would collide with enums.ts's own export at that top-level barrel (TS2308) —
// exactly as schemas/sales.ts already imports `DISPATCH_STATUSES`/`VAT_TREATMENTS`
// rather than redeclaring them.
import { z } from 'zod';
import { moneyCentsSchema, ulidSchema } from './common.js';
import { INVOICE_STATUSES, VAT_TREATMENTS } from '../enums.js';
// Read-only import (not re-exported) of sales.ts's own locally-defined value lists —
// avoids duplicating the PER_LOAD/CONSOLIDATED and WEEKLY/MONTHLY literals here,
// which would drift silently if sales.ts's set ever changed. This does not violate
// the S0 registry rule (which is about EDITING another module's file; importing an
// already-exported const from it is the same read-only relationship every module has
// with `../enums.js`) and creates no barrel collision since invoicing.ts never
// re-exports these names itself.
import { CONSOLIDATION_PERIODS, INVOICING_MODES } from './sales.js';
export const CREDIT_NOTE_STATUSES = ['DRAFT', 'APPROVED', 'SYNCED', 'VOID'];
/** Shared by invoices.xero_sync_state and credit_notes.xero_sync_state (S0/schema
 * header). Not yet ratified in `@zebra/core/enums.ts` (no Xero module built yet). */
export const XERO_SYNC_STATES = ['PENDING', 'SYNCED', 'ERROR'];
// ---------------------------------------------------------------------------
// Shared building blocks
// ---------------------------------------------------------------------------
/** Query-string pagination (cursor + coerced numeric limit), same shape used by
 * every other module's list endpoint (see schemas/sales.ts's matching note). */
const listQueryBaseSchema = z.object({
    cursor: z.string().optional(),
    limit: z.coerce.number().int().positive().max(200).default(50),
});
/** Base UOM quantity, max 3dp, strictly positive — mirrors schemas/sales.ts's
 * `positiveQtySchema` (every invoice/credit-note line carries a positive amount). */
const positiveQtySchema = z
    .string()
    .regex(/^\d+(\.\d{1,3})?$/, 'Invalid decimal quantity (max 3 decimal places)')
    .refine((v) => Number(v) > 0, 'qty must be greater than zero');
// ---------------------------------------------------------------------------
// Raise invoices (GET /invoicing/uninvoiced, POST /invoicing/raise)
// ---------------------------------------------------------------------------
/** Request body for `POST /invoicing/raise`. `periodFrom`/`periodTo` are required
 * (cross-checked in the route handler against each customer's invoicingMode, since
 * that requires a DB lookup zod alone can't do) for CONSOLIDATED customers, and
 * ignored for PER_LOAD customers (spec §8.4 step 4). When both are supplied, `to`
 * must not precede `from`. */
export const raiseInvoicesSchema = z
    .object({
    customerIds: z.array(ulidSchema).min(1, 'at least one customerId is required'),
    periodFrom: z.coerce.date().optional(),
    periodTo: z.coerce.date().optional(),
})
    .superRefine((val, ctx) => {
    if (val.periodFrom && val.periodTo && val.periodTo.getTime() < val.periodFrom.getTime()) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'periodTo must not be before periodFrom', path: ['periodTo'] });
    }
});
export const uninvoicedLineSchema = z.object({
    dispatchLineId: ulidSchema,
    productId: ulidSchema,
    qty: z.string(),
    priceCentsPerUnit: z.string(),
    vatTreatment: z.enum(VAT_TREATMENTS),
});
export const uninvoicedDispatchSchema = z.object({
    dispatchId: ulidSchema,
    number: z.string(),
    dispatchedAt: z.coerce.date(),
    lines: z.array(uninvoicedLineSchema),
});
export const uninvoicedCustomerGroupSchema = z.object({
    customerId: ulidSchema,
    customerCode: z.string(),
    customerName: z.string(),
    invoicingMode: z.enum(INVOICING_MODES),
    consolidationPeriod: z.enum(CONSOLIDATION_PERIODS).nullable(),
    dispatches: z.array(uninvoicedDispatchSchema),
});
export const uninvoicedResponseSchema = z.array(uninvoicedCustomerGroupSchema);
// ---------------------------------------------------------------------------
// Invoices + lines
// ---------------------------------------------------------------------------
export const invoiceLineResponseSchema = z.object({
    id: ulidSchema,
    lineNo: z.number().int().positive(),
    dispatchLineId: ulidSchema.nullable(),
    description: z.string(),
    qty: z.string(),
    unitPriceCents: z.string(),
    vatTreatment: z.enum(VAT_TREATMENTS),
    lineTotalCents: z.string(),
});
export const invoiceResponseSchema = z.object({
    id: ulidSchema,
    tenantId: ulidSchema,
    number: z.string(),
    customerId: ulidSchema,
    status: z.enum(INVOICE_STATUSES),
    issueDate: z.coerce.date(),
    dueDate: z.coerce.date(),
    subtotalCents: z.string(),
    vatCents: z.string(),
    totalCents: z.string(),
    xeroInvoiceId: z.string().nullable(),
    xeroSyncState: z.enum(XERO_SYNC_STATES).nullable(),
    periodFrom: z.coerce.date().nullable(),
    periodTo: z.coerce.date().nullable(),
    pdfKey: z.string().nullable(),
    pdfUrl: z.string().nullable(),
    lines: z.array(invoiceLineResponseSchema),
});
export const raiseInvoicesResponseSchema = z.object({
    invoices: z.array(invoiceResponseSchema),
    summary: z.string(),
});
export const invoiceListQuerySchema = listQueryBaseSchema.extend({
    status: z.enum(INVOICE_STATUSES).optional(),
    customerId: ulidSchema.optional(),
});
export const invoiceApproveResponseSchema = z.object({
    invoice: invoiceResponseSchema,
    pdf: z.object({ key: z.string(), url: z.string().optional() }),
    summary: z.string(),
});
export const invoiceVoidResponseSchema = z.object({
    invoice: invoiceResponseSchema,
});
// ---------------------------------------------------------------------------
// Credit notes + lines
// ---------------------------------------------------------------------------
export const creditNoteLineInputSchema = z.object({
    productId: ulidSchema,
    // Required (cross-field, see creditNoteCreateSchema's superRefine below) when the
    // parent credit note has restock = true; must be omitted/null otherwise.
    locationId: ulidSchema.nullable().optional(),
    description: z.string().min(1).max(500),
    qty: positiveQtySchema,
    unitPriceCents: moneyCentsSchema,
    vatTreatment: z.enum(VAT_TREATMENTS),
});
export const creditNoteCreateSchema = z
    .object({
    invoiceId: ulidSchema,
    reason: z.string().max(500).nullable().optional(),
    restock: z.boolean().optional().default(false),
    lines: z.array(creditNoteLineInputSchema).min(1, 'at least one line is required'),
})
    .superRefine((val, ctx) => {
    if (!val.restock)
        return;
    val.lines.forEach((line, i) => {
        if (line.locationId == null) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'locationId is required on every line when restock = true',
                path: ['lines', i, 'locationId'],
            });
        }
    });
});
export const creditNoteLineResponseSchema = z.object({
    id: ulidSchema,
    lineNo: z.number().int().positive(),
    productId: ulidSchema,
    locationId: ulidSchema.nullable(),
    description: z.string(),
    qty: z.string(),
    unitPriceCents: z.string(),
    vatTreatment: z.enum(VAT_TREATMENTS),
    lineTotalCents: z.string(),
    // Populated only when the parent credit note has restock = true (S1/§9.1) — null
    // otherwise.
    restockValueCents: z.string().nullable(),
});
export const creditNoteResponseSchema = z.object({
    id: ulidSchema,
    tenantId: ulidSchema,
    number: z.string(),
    invoiceId: ulidSchema,
    status: z.enum(CREDIT_NOTE_STATUSES),
    reason: z.string().nullable(),
    subtotalCents: z.string(),
    vatCents: z.string(),
    totalCents: z.string(),
    restock: z.boolean(),
    xeroCreditNoteId: z.string().nullable(),
    xeroSyncState: z.enum(XERO_SYNC_STATES).nullable(),
    pdfKey: z.string().nullable(),
    pdfUrl: z.string().nullable(),
    lines: z.array(creditNoteLineResponseSchema),
});
export const creditNoteListQuerySchema = listQueryBaseSchema.extend({
    status: z.enum(CREDIT_NOTE_STATUSES).optional(),
    invoiceId: ulidSchema.optional(),
});
/** One RETURN_IN `recordMovement` result surfaced back to the caller — mirrors
 * schemas/sales.ts's `dispatchMovementSummarySchema`. Only present when the credit
 * note has restock = true. */
export const creditNoteMovementSummarySchema = z.object({
    lineId: ulidSchema,
    productId: ulidSchema,
    qty: z.string(),
    valueCents: z.string(),
    movementId: ulidSchema,
});
export const creditNoteApproveResponseSchema = z.object({
    creditNote: creditNoteResponseSchema,
    movements: z.array(creditNoteMovementSummarySchema),
    pdf: z.object({ key: z.string(), url: z.string().optional() }),
    summary: z.string(),
});
//# sourceMappingURL=invoicing.js.map